import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../../firebase";
import { signOut } from "firebase/auth";
import { Clock } from "lucide-react";

const SessionTimeout = ({ children }) => {
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // PRODUCTION: 15 minutes session timeout
  const DASHBOARD_TIMEOUT = 15 * 60 * 1000; // 15 minutes

  const timeoutIdRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isShowingModalRef = useRef(false);

  // Check if user is in an active quiz
  const isInActiveQuiz = useCallback(() => {
    return location.pathname.includes('/quiz/') && location.pathname.includes('/take');
  }, [location.pathname]);

  const clearAllTimers = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const handleSessionExpired = useCallback(async () => {
    clearAllTimers();

    try {
      // Clear all session data
      await signOut(auth);
      localStorage.clear();
      sessionStorage.clear();

      // Redirect to login with current path for redirect after login
      const currentPath = location.pathname;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
    } catch (error) {
      navigate("/login", { replace: true });
    }
  }, [navigate, location.pathname, clearAllTimers]);

  const resetTimer = useCallback(() => {
    if (isInActiveQuiz()) {
      clearAllTimers();
      setShowTimeoutModal(false);
      isShowingModalRef.current = false;
      return;
    }

    clearAllTimers();
    setShowTimeoutModal(false);
    isShowingModalRef.current = false;
    lastActivityRef.current = Date.now();

    // Set direct timeout - no warning phase
    timeoutIdRef.current = setTimeout(() => {
      if (!isInActiveQuiz()) {
        isShowingModalRef.current = true;
        setShowTimeoutModal(true);
        // Modal stays until user clicks button
      }
    }, DASHBOARD_TIMEOUT);
  }, [isInActiveQuiz, DASHBOARD_TIMEOUT, clearAllTimers]);

  const handleUserActivity = useCallback(() => {
    // Don't process activity if modal is showing
    if (isShowingModalRef.current) {
      return;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Only reset if enough time has passed (debounce)
    if (timeSinceLastActivity > 1000) {
      lastActivityRef.current = now;
      resetTimer();
    }
  }, [resetTimer]);

  // Setup activity listeners
  useEffect(() => {
    if (!auth.currentUser) {
      return;
    }

    const events = ["mousedown", "mousemove", "keypress", "keydown", "scroll", "touchstart", "click"];

    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Start initial timer
    resetTimer();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      clearAllTimers();
    };
  }, [location.pathname, handleUserActivity, resetTimer, isInActiveQuiz, clearAllTimers]);

  return (
    <>
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>

      {children}

      {/* Session Security Timeout Modal */}
      {showTimeoutModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(12px)',
            zIndex: 999999,
            pointerEvents: 'all',
            animation: 'fadeIn 0.2s ease-out'
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl border border-slate-100/80 max-w-md w-full mx-4 p-6 transform transition-all duration-200 hover:shadow-3xl"
            onClick={(e) => e.stopPropagation()}
            style={{
              pointerEvents: 'all',
              animation: 'slideUp 0.3s ease-out'
            }}
          >
            {/* Icon with depth */}
            <div className="flex justify-center mb-4">
              <div className="bg-slate-50 border border-slate-100 rounded-full w-16 h-16 flex items-center justify-center shadow-sm">
                <Clock className="w-9 h-9 text-slate-400" strokeWidth={1.5} />
              </div>
            </div>

            {/* Title - Security focused */}
            <h2 className="text-xl font-bold text-slate-900 text-center mb-2 tracking-tight">
              Session Security Timeout
            </h2>

            {/* Message - Security reassurance */}
            <p className="text-[15px] text-slate-600 text-center mb-6 leading-relaxed px-2">
              For security purposes, your session has timed out due to a period of inactivity. Please log back in to safely resume your workspace.
            </p>

            {/* Action Button - Premium styling */}
            <button
              onClick={handleSessionExpired}
              className="w-full bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              autoFocus
            >
              Log Back In
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionTimeout;
